import React, { useContext, useEffect } from "react";
import { FoundersType, ResultContext, ResultType } from "../ResultContext";
import { FaTrash } from "react-icons/fa";
import { useUnmount } from "react-use";
import { GivenQuestions } from "../constants/Questions";
import { Label } from "../components/Label";

export function FoundersInput({ questionId }: { questionId: number }) {
  const { result, setResult } = useContext(ResultContext);

  const foundResult = (result[questionId] as {
    name: string;
    type: FoundersType;
  }[]) || [{ name: "", type: FoundersType.NaturalPerson }];

  useEffect(() => {
    if (!result[questionId]) {
      const copy = { ...result };

      copy[questionId] = [
        { name: "", type: FoundersType.NaturalPerson },
        { name: "", type: FoundersType.NaturalPerson },
      ];

      setResult(copy);
    }
    // eslint-disable-next-line
  }, [questionId]);

  const dependingOnQuestions = GivenQuestions.filter(
    (q) => q.dependsOn === questionId
  );

  function removeIndices(indices: number[], copy: Record<number, ResultType>) {
    if (indices.length === 0) {
      return;
    }

    for (const dependingOn of dependingOnQuestions) {
      const currentResult = result[dependingOn.id] as any;

      if (!currentResult) {
        continue;
      }

      if (
        !Array.isArray(currentResult) &&
        currentResult["shares"] !== undefined
      ) {
        copy[dependingOn.id] = {
          ...currentResult,
          shares: currentResult.shares.filter(
            (_: any, index: number) => !indices.includes(index)
          ),
        };

        return;
      }

      copy[dependingOn.id] = currentResult.filter(
        (_: any, index: number) => !indices.includes(index)
      );
    }
  }

  useUnmount(() => {
    const copy = { ...result };

    if (foundResult.length === 0) {
      copy[questionId] = [
        { name: "", type: FoundersType.NaturalPerson },
        { name: "", type: FoundersType.NaturalPerson },
      ];
      setResult(copy);

      return;
    }
    if (foundResult.length === 1) {
      return;
    }

    if (foundResult.length === 2) {
      return;
    }

    const indicesToRemove: number[] = [];

    copy[questionId] = foundResult.filter((item, index) => {
      const result = item.name.trim() !== "";

      if (!result) {
        indicesToRemove.push(index);
      }

      return result;
    });

    removeIndices(indicesToRemove, copy);

    setResult(copy);
  });

  return (
    <div className="flex flex-column">
      {foundResult.map((item, index) => {
        return (
          <div className={`${index !== 0 ? "mt-4" : ""}`} key={index}>
            <Label
              title={`Founder ${index + 1}` || ""}
              information={
                <div className="text-justify">
                  Include first and last name in case you will hold or hold the
                  shares as a natural person; or include company legal name and
                  legal form in case you will hold or hold the shares with a
                  company.
                </div>
              }
            />
            <div className="flex items-center">
              <input
                type="text"
                placeholder={"Name"}
                value={item.name}
                onChange={(e) => {
                  const copy = { ...result };

                  copy[questionId] = foundResult.map((it, id) => {
                    if (id === index) {
                      return {
                        ...it,
                        name: e.target.value,
                      };
                    }

                    return it;
                  });

                  setResult(copy);
                }}
              />
              <select
                className="ml-4 mr-4"
                value={item.type}
                onChange={(e) => {
                  const copy = { ...result };

                  copy[questionId] = foundResult.map((it, id) => {
                    if (id === index) {
                      return {
                        ...it,
                        type: e.target.value as FoundersType,
                      };
                    }

                    return it;
                  });

                  setResult(copy);
                }}
              >
                {Object.values(FoundersType).map((item, i) => {
                  return (
                    <option value={item} key={i}>
                      {item}
                    </option>
                  );
                })}
              </select>
              {index > 1 ? (
                <div
                  className="ml-2 pointer red"
                  onClick={() => {
                    const copy = { ...result };

                    copy[questionId] = foundResult.filter((_, id) => {
                      return id !== index;
                    });

                    removeIndices([index], copy);

                    setResult(copy);
                  }}
                >
                  <FaTrash />
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
      {foundResult.length < 6 ? (
        <div
          className="button w-fit mt-2"
          onClick={() => {
            const copy = { ...result };

            copy[questionId] = foundResult.concat({
              name: "",
              type: FoundersType.NaturalPerson,
            });

            setResult(copy);
          }}
        >
          Add founder
        </div>
      ) : null}
    </div>
  );
}
